"use client";

import { useState } from "react";
import { Frame, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Déplacez une seule ligne, et les compteurs cessent d'être indépendants",
        factory: "creerCompteur",
        prefix: "compteur",
        modeClosure: "total dans la fabrique",
        tagClosure: "closure",
        modeShared: "total en dehors",
        tagShared: "variable partagée",
        create: "Créer un compteur",
        reset: "Réinitialiser",
        calls: (n) => (n <= 1 ? `${n} appel` : `${n} appels`),
        measure: (total, ecart) =>
            `${total <= 1 ? `${total} appel` : `${total} appels`} au total, écart entre compteurs : ${ecart}`,
        zero: (nom, total) =>
            `${nom} affiche 0 alors que ${total} appels ont déjà eu lieu à côté. Sa variable total vient de naître avec lui : elle ne sait rien de ce qui s'est passé avant.`,
        spread: (ecart) =>
            `Les compteurs sortent de la même fabrique et pourtant leurs valeurs sont séparées de ${ecart}. Chacun garde sa propre variable total, vivante bien après la fin de creerCompteur().`,
        equal: "Ils affichent le même nombre parce qu'ils ont reçu le même nombre d'appels. Déplacez la ligne pour voir la différence : un seul total encaisserait les deux séries.",
        sharedGhost: (nom, total) =>
            `${nom} affiche ${total} sans avoir jamais été appelé. Il lit le total des autres : la ligne déplacée a supprimé les environnements séparés.`,
        sharedAll: (total) =>
            `Les ${total} appels tombent dans le même total. Les compteurs bougent ensemble, peu importe lequel vous cliquez.`,
        note: "Chaque ligne affiche la valeur de la variable total que ce compteur lit, et non la dernière valeur qu'il a renvoyée : c'est ce qui rend visible un compteur jamais appelé. Changer de version rejoue la même suite d'appels sur l'autre code, sans rien réinitialiser ; dans un vrai programme, il faudrait relancer la page pour changer d'implémentation. Les barres sont à l'échelle de 12 appels, puis du total dès qu'il dépasse 12, et les valeurs exactes sont écrites à côté.",
    },
    en: {
        title: "Move a single line, and the counters stop being independent",
        factory: "createCounter",
        prefix: "counter",
        modeClosure: "total inside the factory",
        tagClosure: "closure",
        modeShared: "total outside",
        tagShared: "shared variable",
        create: "Create a counter",
        reset: "Reset",
        calls: (n) => (n === 1 ? "1 call" : `${n} calls`),
        measure: (total, ecart) =>
            `${total === 1 ? "1 call" : `${total} calls`} in total, spread between counters: ${ecart}`,
        zero: (nom, total) =>
            `${nom} shows 0 while ${total} calls have already happened next to it. Its total variable was born with it: it knows nothing about what came before.`,
        spread: (ecart) =>
            `The counters come out of the same factory, yet their values are ${ecart} apart. Each one keeps its own total variable, alive long after createCounter() returned.`,
        equal: "They show the same number because they received the same number of calls. Move the line to see the difference: a single total would absorb both series.",
        sharedGhost: (nom, total) =>
            `${nom} shows ${total} without ever being called. It reads the same total as the others: moving that line removed the separate environments.`,
        sharedAll: (total) =>
            `All ${total} calls land in the same total. The counters move together, whichever one you click.`,
        note: "Each row shows the value of the total variable that this counter reads, not the last value it returned: that is what makes a never-called counter visible. Switching versions replays the same sequence of calls against the other code without resetting anything; in a real program you would have to reload the page to change implementation. The bars are scaled to 12 calls, then to the total once it goes past 12, and the exact values are printed next to them.",
    },
};

const LETTRES = ["A", "B", "C", "D"];
const MAX = 4;
// Deux compteurs déjà appelés, pour que la démo dise quelque chose dès le chargement.
// 7 contre 1 plutôt que 6 contre 2 : au chargement, les barres font 58,3 % et 8,3 %,
// soit un facteur 7. Passer en version partagée les met toutes deux à 66,7 %, donc la
// petite barre est multipliée par 8 sous les yeux du lecteur. À 6 contre 2, l'écart
// interne tombait à un facteur 3 tout juste, la limite de ce que l'œil distingue.
// La somme vaut 8 et rien ne la fait redescendre : les verdicts peuvent écrire « appels » au pluriel.
const DEPART = [7, 1];
const ECHELLE = 12; // plancher de l'échelle des barres

export default function ClosureCounters({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [partagee, setPartagee] = useState(false);
    const [appels, setAppels] = useState(DEPART);

    const total = appels.reduce((somme, n) => somme + n, 0);

    // Le même historique d'appels, relu par les deux versions du code.
    // Avec total dans la fabrique, chaque compteur lit sa propre variable.
    // Avec total en dehors, tous lisent la seule qui existe.
    const valeurs = partagee ? appels.map(() => total) : appels.slice();

    const haut = Math.max(...valeurs);
    const bas = Math.min(...valeurs);
    const ecart = haut - bas;
    // Plancher à 12 appels pour que l'état de départ ne remplisse pas déjà le cadre,
    // puis le total dès qu'il le dépasse. Avec une échelle fixe, une suite comme
    // [12, 20] saturait les deux barres à 100 % pendant que le verdict annonçait un
    // écart de 8, et [20, 20, 20, 20] dessinait exactement le même visuel dans les
    // deux versions : la démo ne montrait plus rien. Ici les deux versions ne peuvent
    // plus coïncider, la version partagée remplissant toujours toute la largeur.
    const echelle = Math.max(ECHELLE, total);
    const largeur = (v) => `${(v / echelle) * 100}%`;

    const vierge = appels.findIndex((n) => n === 0);
    const nomVierge = vierge === -1 ? "" : `${t.prefix}${LETTRES[vierge]}`;

    // Le total ne peut jamais valoir 0 : la démo démarre à 8 appels et rien ne fait
    // redescendre un compteur. Les deux branches « rien n'a encore été appelé » ont
    // donc été retirées plutôt que de laisser deux libellés traduits jamais affichés.
    let ton;
    let verdict;
    if (partagee) {
        ton = "bad";
        verdict =
            vierge !== -1 ? t.sharedGhost(nomVierge, total) : t.sharedAll(total);
    } else if (vierge !== -1) {
        ton = "good";
        verdict = t.zero(nomVierge, total);
    } else if (ecart > 0) {
        ton = "good";
        verdict = t.spread(ecart);
    } else {
        ton = "neutral";
        verdict = t.equal;
    }

    const lignes = partagee
        ? [
              { texte: "let total = 0;", fort: true },
              { texte: `function ${t.factory}() {`, fort: false },
              { texte: "  return () => (total += 1);", fort: false },
              { texte: "}", fort: false },
          ]
        : [
              { texte: `function ${t.factory}() {`, fort: false },
              { texte: "  let total = 0;", fort: true },
              { texte: "  return () => (total += 1);", fort: false },
              { texte: "}", fort: false },
          ];

    const bouton = (actif, libelle, tag, onClick) => (
        <button
            onClick={onClick}
            className={`rounded-xl border px-3 py-2.5 text-left transition-colors duration-200 ${
                actif
                    ? "border-primary bg-primary/[0.06] dark:bg-primary/10"
                    : "border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20"
            }`}
        >
            <span
                className={`block text-xs font-bold leading-snug ${
                    actif ? "text-primary" : "text-gray-700 dark:text-grayDark-200"
                }`}
            >
                {libelle}
            </span>
            <span className="mt-1 block text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                {tag}
            </span>
        </button>
    );

    return (
        <Frame icon="copy-linear" title={t.title}>
            <div className="grid grid-cols-2 gap-2">
                {bouton(!partagee, t.modeClosure, t.tagClosure, () => setPartagee(false))}
                {bouton(partagee, t.modeShared, t.tagShared, () => setPartagee(true))}
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl bg-gray-50 px-3 py-3 dark:bg-white/[0.04]">
                {lignes.map((ligne) => (
                    <div
                        key={ligne.texte}
                        className={`whitespace-pre font-geistMono text-xs leading-6 ${
                            ligne.fort
                                ? "font-bold text-primary"
                                : "text-gray-600 dark:text-lightBlue"
                        }`}
                    >
                        {ligne.texte}
                    </div>
                ))}
            </div>

            <div className="mt-5 space-y-3">
                {appels.map((n, i) => (
                    <div key={LETTRES[i]} className="flex items-center gap-2 sm:gap-3">
                        <div className="w-20 shrink-0">
                            <div className="font-geistMono text-xs dark:text-white">
                                {t.prefix}
                                {LETTRES[i]}
                            </div>
                            <div className="text-[11px] text-gray-400 dark:text-grayDark-500">
                                {t.calls(n)}
                            </div>
                        </div>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.08]">
                            <div
                                className={`h-full rounded-full transition-all duration-300 ${
                                    partagee
                                        ? "bg-gray-400 dark:bg-grayDark-500"
                                        : "bg-primary"
                                }`}
                                style={{ width: largeur(valeurs[i]) }}
                            />
                        </div>
                        <span className="w-8 shrink-0 text-right text-sm font-bold tabular-nums dark:text-white">
                            {valeurs[i]}
                        </span>
                        <button
                            aria-label={`${t.prefix}${LETTRES[i]}()`}
                            onClick={() =>
                                setAppels((v) => v.map((x, j) => (j === i ? x + 1 : x)))
                            }
                            className="shrink-0 rounded-lg bg-primary px-2.5 py-1.5 font-geistMono text-xs font-bold text-white transition-opacity duration-200 hover:opacity-90"
                        >
                            +1
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    onClick={() => setAppels((v) => [...v, 0])}
                    disabled={appels.length >= MAX}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 transition-colors duration-200 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/20"
                >
                    {t.create}
                </button>
                <button
                    onClick={() => setAppels(DEPART)}
                    className="rounded-lg px-3 py-2 text-xs text-gray-500 underline transition-colors duration-200 hover:text-gray-700 dark:text-grayDark-400 dark:hover:text-grayDark-200"
                >
                    {t.reset}
                </button>
            </div>

            <div className="mt-4 text-sm text-gray-500 dark:text-lightBlue/80">
                {t.measure(total, ecart)}
            </div>

            <Verdict tone={ton}>{verdict}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
